import {
  TimeLine,
  EventData,
  Work,
  EventLog,
  WorkItemList,
  EventDataTypeEnum,
  EventLogTypeEnum,
  WorkItemTypeEnum,
  WorkItemStateEnum,
} from "../store/services/timelineApiRTK";
import {
  TimelineItem,
  ProcessedTimelineItem,
  TimelineFilters,
} from "../types/Timeline.types";
import { parseEventData } from "./eventDataParser";

// Convert timeline data to unified timeline items
export const processTimelineData = (timeline: TimeLine): TimelineItem[] => {
  const items: TimelineItem[] = [];

  // Process EventData
  timeline.Datas.forEach((data) => {
    items.push({
      id: `eventdata-${data.Id}`,
      type: 'eventdata',
      timestamp: data.Time,
      data,
    });
  });

  // Process Works
  timeline.Works.forEach((work) => {
    items.push({
      id: `work-${work.Id}`,
      type: 'work',
      timestamp: work.Created,
      data: work,
    });
  });

  // Process EventLogs
  timeline.EventLogs.forEach((log) => {
    items.push({
      id: `eventlog-${log.Id}`,
      type: 'eventlog',
      timestamp: log.Time,
      data: log,
    });
  });

  // Process WorkItems
  timeline.WorkItems.forEach((workItem) => {
    items.push({
      id: `workitem-${workItem.Id}`,
      type: 'workitem',
      timestamp: workItem.Created,
      data: workItem,
    });
  });

  // Sort by timestamp (newest first)
  return items.sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

// Process timeline items with display information
export const enrichTimelineItems = (items: TimelineItem[]): ProcessedTimelineItem[] => {
  return items.map((item) => {
    switch (item.type) {
      case 'eventdata':
        return enrichEventData(item);
      case 'work':
        return enrichWork(item);
      case 'eventlog':
        return enrichEventLog(item);
      case 'workitem':
        return enrichWorkItem(item);
      default:
        return item as ProcessedTimelineItem;
    }
  });
};

// Enrich EventData items
const enrichEventData = (item: TimelineItem): ProcessedTimelineItem => {
  const eventData = item.data as EventData;
  const processed = parseEventData(eventData);
  
  return {
    ...item,
    title: processed.displayName,
    description: getEventDataDescription(eventData),
    icon: getEventDataIcon(eventData.Type),
    color: getEventDataColor(eventData.Type),
    metadata: {
      createdBy: eventData.CreatedBy.Name,
      hasAttachments: false, // Could be enhanced based on type
    },
  };
};

// Enrich Work items
const enrichWork = (item: TimelineItem): ProcessedTimelineItem => {
  const work = item.data as Work;
  
  return {
    ...item,
    title: `Ärende: ${work.Name}`,
    description: work.Description || "Inget beskrivning tillgänglig",
    icon: "Work",
    color: getWorkStateColor(work.State),
    actionable: true,
    metadata: {
      category: work.Category.Name,
      assignedTo: work.AssignedTo?.Name,
      team: work.Team.Name,
      state: work.State,
      hasUnansweredEmail: work.HasUnansweredEmail,
    },
  };
};

// Enrich EventLog items
const enrichEventLog = (item: TimelineItem): ProcessedTimelineItem => {
  const log = item.data as EventLog;
  
  return {
    ...item,
    title: getEventLogTitle(log.Type),
    description: log.Description,
    icon: getEventLogIcon(log.Type),
    color: "info.main",
    metadata: {
      createdBy: log.CreatedBy.Name,
      type: log.Type,
    },
  };
};

// Enrich WorkItem items
const enrichWorkItem = (item: TimelineItem): ProcessedTimelineItem => {
  const workItem = item.data as WorkItemList;
  
  return {
    ...item,
    title: getWorkItemTitle(workItem.Type),
    description: workItem.Description || workItem.Name,
    icon: getWorkItemIcon(workItem.Type),
    color: getWorkItemStateColor(workItem.State),
    actionable: workItem.State === WorkItemStateEnum.Active,
    metadata: {
      assignedTo: workItem.AssignedTo?.Name,
      team: workItem.Team.Name,
      scheduledTo: workItem.ScheduledTo,
      state: workItem.State,
      workId: workItem.WorkId,
    },
  };
};

// Helper functions for EventData
const getEventDataDescription = (eventData: EventData): string => {
  switch (eventData.Type) {
    case EventDataTypeEnum.Text:
      try {
        const parsed = JSON.parse(eventData.Data);
        return parsed.Description || "Tom kommentar";
      } catch {
        return "Kommentar";
      }
    case EventDataTypeEnum.IncomingEmail:
    case EventDataTypeEnum.OutgoingEmail:
    case EventDataTypeEnum.UnprocessedEmail:
      try {
        const parsed = JSON.parse(eventData.Data);
        return `${parsed.Subject || "Inget ämne"} (${parsed.From} → ${parsed.To})`;
      } catch {
        return "E-post";
      }
    case EventDataTypeEnum.FileCrmDocument:
      try {
        const parsed = JSON.parse(eventData.Data);
        return `Fil: ${parsed.OrginalFilename || "Okänd fil"}`;
      } catch {
        return "Fil";
      }
    default:
      return eventData.Name || "Händelse";
  }
};

const getEventDataIcon = (type: EventDataTypeEnum): string => {
  switch (type) {
    case EventDataTypeEnum.Text: return "Comment";
    case EventDataTypeEnum.IncomingEmail: return "Email";
    case EventDataTypeEnum.OutgoingEmail: return "Send";
    case EventDataTypeEnum.UnprocessedEmail: return "EmailOutlined";
    case EventDataTypeEnum.FileCrmDocument: return "AttachFile";
    case EventDataTypeEnum.SurveyResponse: return "Quiz";
    case EventDataTypeEnum.Call: return "Phone";
    case EventDataTypeEnum.OutgoingCall: return "PhoneForwarded";
    default: return "Event";
  }
};

const getEventDataColor = (type: EventDataTypeEnum): string => {
  switch (type) {
    case EventDataTypeEnum.Text: return "info.main";
    case EventDataTypeEnum.IncomingEmail: return "primary.main";
    case EventDataTypeEnum.OutgoingEmail: return "success.main";
    case EventDataTypeEnum.UnprocessedEmail: return "warning.main";
    case EventDataTypeEnum.FileCrmDocument: return "success.main";
    case EventDataTypeEnum.Call:
    case EventDataTypeEnum.OutgoingCall: return "secondary.main";
    default: return "grey.500";
  }
};

// Helper functions for Work
const getWorkStateColor = (state: number): string => {
  switch (state) {
    case 1: return "success.main"; // Active
    case 2: return "grey.500";     // Closed
    default: return "warning.main";
  }
};

// Helper functions for EventLog
const getEventLogTitle = (type: EventLogTypeEnum): string => {
  switch (type) {
    case EventLogTypeEnum.AssignWorkItem: return "Aktivitet tilldelad";
    case EventLogTypeEnum.AssignWork: return "Ärende tilldelat";
    case EventLogTypeEnum.UpdateObject: return "Objekt uppdaterat";
    case EventLogTypeEnum.ReopenWork: return "Ärende återöppnat";
    case EventLogTypeEnum.ChangeCategory: return "Kategori ändrad";
    case EventLogTypeEnum.WorkChangeState: return "Ärende status ändrad";
    case EventLogTypeEnum.UpdateCustomerSalesPerson: return "Säljare uppdaterad";
    case EventLogTypeEnum.ChangeTeam: return "Team ändrat";
    default: return "Systemhändelse";
  }
};

const getEventLogIcon = (type: EventLogTypeEnum): string => {
  switch (type) {
    case EventLogTypeEnum.AssignWorkItem:
    case EventLogTypeEnum.AssignWork: return "Assignment";
    case EventLogTypeEnum.UpdateObject: return "Edit";
    case EventLogTypeEnum.ReopenWork: return "Replay";
    case EventLogTypeEnum.ChangeCategory: return "Category";
    case EventLogTypeEnum.WorkChangeState: return "ChangeCircle";
    case EventLogTypeEnum.UpdateCustomerSalesPerson: return "Person";
    case EventLogTypeEnum.ChangeTeam: return "Group";
    default: return "Settings";
  }
};

// Helper functions for WorkItem
const getWorkItemTitle = (type: WorkItemTypeEnum): string => {
  switch (type) {
    case WorkItemTypeEnum.OpportunityCall: return "Affärsmöjlighet - Samtal";
    case WorkItemTypeEnum.HandleEmail: return "Hantera e-post";
    case WorkItemTypeEnum.PhysicalMeeting: return "Fysiskt möte";
    case WorkItemTypeEnum.PhoneCall: return "Telefonsamtal";
    case WorkItemTypeEnum.Email: return "E-post";
    case WorkItemTypeEnum.Sms: return "SMS";
    case WorkItemTypeEnum.WelcomeCall: return "Välkomstsamtal";
    case WorkItemTypeEnum.DigitalMeeting: return "Digitalt möte";
    case WorkItemTypeEnum.FollowUpEmail: return "Uppföljning - E-post";
    case WorkItemTypeEnum.FollowUpPhoneCall: return "Uppföljning - Samtal";
    default: return "Aktivitet";
  }
};

const getWorkItemIcon = (type: WorkItemTypeEnum): string => {
  switch (type) {
    case WorkItemTypeEnum.OpportunityCall:
    case WorkItemTypeEnum.PhoneCall:
    case WorkItemTypeEnum.WelcomeCall:
    case WorkItemTypeEnum.FollowUpPhoneCall: return "Phone";
    case WorkItemTypeEnum.HandleEmail:
    case WorkItemTypeEnum.Email:
    case WorkItemTypeEnum.FollowUpEmail: return "Email";
    case WorkItemTypeEnum.PhysicalMeeting: return "MeetingRoom";
    case WorkItemTypeEnum.DigitalMeeting: return "VideoCall";
    case WorkItemTypeEnum.Sms: return "Sms";
    default: return "Task";
  }
};

const getWorkItemStateColor = (state: WorkItemStateEnum): string => {
  switch (state) {
    case WorkItemStateEnum.Active: return "warning.main";
    case WorkItemStateEnum.Closed: return "success.main";
    default: return "grey.500";
  }
};

// Filter timeline items
export const filterTimelineItems = (
  items: ProcessedTimelineItem[],
  filters: TimelineFilters
): ProcessedTimelineItem[] => {
  return items.filter((item) => {
    // Type filters
    if (!filters.showEventData && item.type === 'eventdata') return false;
    if (!filters.showWorks && item.type === 'work') return false;
    if (!filters.showEventLogs && item.type === 'eventlog') return false;
    if (!filters.showWorkItems && item.type === 'workitem') return false;

    // Date range filter
    if (filters.dateRange) {
      const itemDate = new Date(item.timestamp);
      if (itemDate < filters.dateRange.from || itemDate > filters.dateRange.to) {
        return false;
      }
    }

    return true;
  });
};